import React, {useState, useEffect} from "react"
import Flex from "@irati/ui/src/Flex"
import moment, {Moment} from "moment"
import momentTZ from "moment-timezone"
import Validation from "@irati/utils/validation"
import {Radio, Input} from "@irati/ui/src/FormItem"
import Expander, {ExpanderArrow} from "@irati/ui/src/Expander"
import FormattedMessage, {injectIntl} from "@irati/utils/lang"
import {InjectIntlProps} from "@irati/utils/lang/injectIntl"
import mainTheme from "@irati/ui/src/global/theme"
import ReactDatePicker from "react-datepicker"
import TimezoneSelect from "@irati-web/fields/recognition/TimezoneSelect"
import {Body} from "@irati/ui/src/Typography"
import {IconButton, IconTypes} from "@irati/ui/src/Button"
import {DatePickerContainer, StyledForm} from "./styled"

interface Props extends InjectIntlProps {
  defaultValue: {date: string | Moment | momentTZ.Moment; timezone: string}
  onChange: (utc: string, timezone: string) => void
  onScheduleOptionChange?: (isScheduled: boolean) => void
  forced?: boolean
  label?: string
  openBelow?: boolean
}
enum SchedulingType {
  NOW,
  LATER
}
const SchedulerRadioOptions = [
  {
    label: <FormattedMessage id="recognition.create.sendNow.label" />,
    value: SchedulingType.NOW
  },
  {
    label: <FormattedMessage id="recognition.create.sendLater.label" />,
    value: SchedulingType.LATER
  }
]
const CustomDatetimePickerHeader = (props) => {
  const {monthDate, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled} = props
  return (
    <Flex alignItems="center" justifyContent="center" gap={2}>
      {!prevMonthButtonDisabled && <IconButton icon={IconTypes.chevronLeft} onClick={decreaseMonth} />}
      <Body>
        {monthDate.toLocaleString("en-UK", {
          month: "long",
          year: "numeric"
        })}
      </Body>
      {!nextMonthButtonDisabled && <IconButton icon={IconTypes.chevronRight} onClick={increaseMonth} />}
    </Flex>
  )
}
const DateSelector: React.FC<{isOpen: boolean; date?: string; isPrior: boolean}> = (props) => {
  const {isOpen, date, isPrior} = props
  return (
    <StyledForm>
      <Input
        name="scheduledAt"
        hideLabel
        flush
        addon={{
          after: <ExpanderArrow isExpanded={isOpen} size={12} color={mainTheme.colors.greys.mediumLight} />
        }}
        value={date ? date : ""}
        placeholder="recognition.create.dateTime.label"
        validation={Validation.string("recognition.validation.required").test(
          "Scheduled date after current date",
          "recognition.validation.priorDate",
          () => !isPrior
        )}
      />
    </StyledForm>
  )
}
const getStartDate = (timezone: string) => {
  const now = momentTZ().tz(timezone)
  now.seconds(0)
  now.milliseconds(0)
  if (now.minute() <= 30) return now.set({minute: 30})
  if (now.minute() > 30) return now.set({hour: now.hours() + 1, minute: 0})
  return now
}
const getDateWithTimezone = (date: Moment, timezone: string) => {
  return momentTZ.tz(date, timezone).date(date.date()).hour(date.hour()).minute(date.minute())
}
const Scheduler: React.FC<Props> = (props) => {
  const {formatMessage, onChange, onScheduleOptionChange, defaultValue, label, forced = false} = props
  const [schedulingType, setSchedulingType] = useState<SchedulingType>(
    defaultValue?.date || forced ? SchedulingType.LATER : SchedulingType.NOW
  )
  const [isPrior, setIsPrior] = useState(false)
  const [isDateExpanded, setIsDateExpanded] = useState(false)
  const [timezone, setTimezone] = useState(defaultValue?.timezone || momentTZ.tz.guess())
  const [selectedDate, setSelectedDate] = useState<Moment>(
    defaultValue?.date ? momentTZ.tz(defaultValue?.date, timezone) : getStartDate(timezone)
  )
  useEffect(() => {
    if (!selectedDate || schedulingType === SchedulingType.NOW) {
      onChange?.("", "")
      return
    }
    const newSelectedDate = getDateWithTimezone(selectedDate, timezone)
    if (newSelectedDate.toDate() < getStartDate(timezone).toDate()) {
      onChange?.("", timezone)
      setIsPrior(true)
      return
    }
    setIsPrior(false)
    const utc = newSelectedDate.clone().utc().format()
    onChange?.(utc, timezone)
  }, [selectedDate, timezone, schedulingType])
  const handleDateChange = (value: Moment) => {
    const newValue = moment(value)
    const localizedDate = getDateWithTimezone(newValue, timezone)
    if (localizedDate.toDate() < getStartDate(timezone).toDate()) {
      return
    }
    setSelectedDate(newValue)
  }
  const minDate = () => {
    return getStartDate(timezone).clone()
  }
  const onSchedulingTypeChange = (value: SchedulingType) => {
    setSchedulingType(value)
    onScheduleOptionChange?.(value === SchedulingType.LATER)
  }
  const handleTimeClass = (timeSlot: Date) => {
    const time = moment(timeSlot)
    const localizedDate = getDateWithTimezone(time, timezone)
    if (localizedDate.toDate() < getStartDate(timezone).toDate()) {
      return "react-datepicker__time-list-item--disabled"
    }
    return ""
  }
  return (
    <Flex direction="column">
      {label && (
        <Body small>
          <FormattedMessage id={label} />
        </Body>
      )}
      {!forced && (
        <Flex>
          <Radio name="schedulerOption" inline value={schedulingType} options={SchedulerRadioOptions} onChange={onSchedulingTypeChange} />
        </Flex>
      )}
      {schedulingType === SchedulingType.LATER && (
        <>
          <Expander
            HeaderLabel={() => (
              <DateSelector date={selectedDate?.format("Do, MMMM YYYY, h:mm A")} isOpen={isDateExpanded} isPrior={isPrior} />
            )}
            showArrow={false}
            above={!props.openBelow}
            absolute
            fullWidth
            clickOut
            onChange={setIsDateExpanded}
            showText={formatMessage({id: "recognition.create.scheduleDateTime.label"})}
            iconSize={12}>
            <DatePickerContainer>
              <ReactDatePicker
                selected={selectedDate?.toDate()}
                inline
                showTimeSelect
                renderCustomHeader={CustomDatetimePickerHeader}
                timeClassName={handleTimeClass}
                calendarStartDay={1}
                maxDate={minDate().clone().add(1, "months").toDate()}
                minDate={minDate().toDate()}
                onChange={handleDateChange}
              />
            </DatePickerContainer>
          </Expander>
          <TimezoneSelect onChange={setTimezone} value={timezone} />
        </>
      )}
    </Flex>
  )
}

export default injectIntl(Scheduler)
